/**
 * Sequence-diagram slice for the diagram store.
 *
 * This module owns every action that touches sequence-diagram entities
 * (participants, messages, fragments, and participant groups) plus the
 * private helpers that keep fragment bounds and alt-divider positions in
 * sync.  It is consumed by `diagramStore` via {@link createSequenceActions}.
 *
 * The slice is intentionally decoupled from the parent store: it only
 * requires that callers provide an updater function (which wraps Immer's
 * `produce`) and a state shape that matches {@link SequenceMutableState}.
 * That keeps slice logic easy to test in isolation and prevents circular
 * imports between the slice and the host store.
 */
import { generateId } from '../../utils';
import { getNextParticipantColor } from '../../utils/color';
import { nextSequenceMessageY } from '../../utils/sequenceMessageLayout';
import { SEQUENCE_PARTICIPANT_COLORS } from '../../constants/sequence';
import {
  applyFragmentNesting,
  assignBelongingMessagesInFragment,
  recalcFragmentBounds,
  resolveStickyParentFragmentId,
} from '../../utils/fragmentLayout';
import type {
  CatalogComponent,
  DiagramPage,
  DiagramType,
  Graph,
  Node,
  SequenceFragment,
  SequenceMessage,
  SequenceParticipant,
  SequenceParticipantGroup,
} from '../../types';
import { enrollParticipant, mergeIntoCatalog, syncCatalogAcrossDocument } from '../../utils/catalog';

/** State fields that the sequence slice reads or mutates. */
export interface SequenceMutableState {
  diagramType: DiagramType;
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength: number;
  pages: DiagramPage[];
  activePage: string;
  activeLayerId: string;
  catalog: CatalogComponent[];
  nodes: Node[];
  graphs: Record<string, Graph>;
}

/** Patch shape accepted by `applySequenceLayoutPatch`. */
export interface SequenceLayoutPatch {
  messageYUpdates?: Array<{ id: string; y: number }>;
  messageFragmentUpdates?: Array<{ id: string; fragmentId?: string }>;
  fragmentUpdates?: Array<{ id: string; startY?: number; endY?: number; altDividerY?: number }>;
  sortMessages?: boolean;
  recalcMessageFragments?: boolean;
  recalcFragmentIds?: string[];
}

/** Public action surface contributed by the sequence slice. */
export interface SequenceActions {
  setDiagramType: (type: DiagramType) => void;
  setSeqLifelineLength: (length: number) => void;
  addParticipant: (
    participant: Pick<SequenceParticipant, 'label'> &
      Partial<Omit<SequenceParticipant, 'id' | 'order' | 'label'>>,
  ) => void;
  updateParticipant: (id: string, updates: Partial<SequenceParticipant>) => void;
  removeParticipant: (id: string) => void;
  reorderParticipants: () => void;
  addMessage: (message: Omit<SequenceMessage, 'id' | 'y'> & { y?: number }) => void;
  updateMessage: (id: string, updates: Partial<SequenceMessage>) => void;
  applySequenceLayoutPatch: (patch: SequenceLayoutPatch) => void;
  removeMessage: (id: string) => void;
  addFragment: (fragment: Omit<SequenceFragment, 'id'>) => void;
  updateFragment: (id: string, updates: Partial<SequenceFragment>) => void;
  removeFragment: (id: string) => void;
  moveFragment: (id: string, deltaY: number) => void;
  groupParticipants: (participantIds: string[], label?: string) => void;
  ungroupParticipants: (groupId: string) => void;
  toggleParticipantGroupCollapse: (groupId: string) => void;
  updateParticipantGroup: (id: string, updates: Partial<SequenceParticipantGroup>) => void;
  replaceSequenceContent: (data: {
    participants: SequenceParticipant[];
    messages: SequenceMessage[];
    fragments: SequenceFragment[];
    participantGroups?: SequenceParticipantGroup[];
    seqLifelineLength?: number;
  }) => void;
}

/** Updater signature compatible with `set(produce(...))`. */
export type SequenceStateUpdater<TState extends SequenceMutableState> = (
  mutator: (state: TState) => void,
) => void;

function moveFragmentRecursive<TState extends SequenceMutableState>(
  state: TState,
  id: string,
  deltaY: number,
): void {
  const frag = state.fragments.find((f) => f.id === id);
  if (!frag) return;
  frag.startY += deltaY;
  frag.endY += deltaY;
  if (frag.type === 'alt' && frag.altDividerY !== undefined) {
    frag.altDividerY += deltaY;
  }
  state.messages.forEach((m) => {
    if (m.fragmentId === id) m.y += deltaY;
  });
  state.fragments
    .filter((f) => f.parentFragmentId === id)
    .forEach((child) => moveFragmentRecursive(state, child.id, deltaY));
}

function nextParticipantColor(participants: SequenceParticipant[]): string {
  return getNextParticipantColor(
    participants.map((p) => p.color),
    SEQUENCE_PARTICIPANT_COLORS,
  );
}

export function syncParticipantOrder(participants: SequenceParticipant[]): void {
  participants.sort((a, b) => a.x - b.x || a.order - b.order);
  participants.forEach((participant, idx) => {
    participant.order = idx;
  });
}

export function detachParticipantFromFragments(
  fragments: SequenceFragment[],
  participantId: string,
): SequenceFragment[] {
  return fragments
    .map((fragment) => ({
      ...fragment,
      participantIds: fragment.participantIds.filter((id) => id !== participantId),
    }))
    .filter((fragment) => fragment.participantIds.length > 0);
}

function syncSequenceToActivePage<TState extends SequenceMutableState>(state: TState): void {
  const page = state.pages.find((entry) => entry.id === state.activePage);
  if (!page) return;
  page.participants = state.participants.map((participant) => ({ ...participant }));
  page.messages = state.messages.map((message) => ({ ...message }));
  page.fragments = state.fragments.map((fragment) => ({ ...fragment }));
  page.participantGroups = state.participantGroups.map((group) => ({
    ...group,
    childParticipantIds: [...group.childParticipantIds],
  }));
  page.seqLifelineLength = state.seqLifelineLength;
}

/**
 * Build the sequence-diagram action set bound to the host store's updater.
 *
 * `applyUpdate` is expected to wrap Immer's `produce` so the supplied
 * mutator can mutate state directly while still returning a new immutable
 * snapshot upstream.
 */
export function createSequenceActions<TState extends SequenceMutableState>(
  applyUpdate: SequenceStateUpdater<TState>,
): SequenceActions {
  const apply: SequenceStateUpdater<TState> = (mutator) => {
    applyUpdate((state) => {
      mutator(state);
      syncSequenceToActivePage(state);
    });
  };

  return {
    setDiagramType: (type) =>
      apply((state) => {
        state.diagramType = type;
        const activePage = state.pages.find((page) => page.id === state.activePage);
        if (activePage) activePage.diagramType = type;
      }),

    setSeqLifelineLength: (length) =>
      apply((state) => {
        state.seqLifelineLength = length;
        const activePage = state.pages.find((page) => page.id === state.activePage);
        if (activePage) activePage.seqLifelineLength = length;
      }),

    addParticipant: (participantData) =>
      apply((state) => {
        const lastP =
          state.participants.length > 0
            ? state.participants.reduce((a, b) => (a.x > b.x ? a : b))
            : null;
        const x = participantData.x ?? (lastP ? lastP.x + 200 : 150);
        const layerId = state.activeLayerId === 'default' ? undefined : state.activeLayerId;
        state.participants.push({
          ...participantData,
          id: generateId('participant'),
          type: participantData.type?.trim() ? participantData.type : 'service',
          color: participantData.color ?? nextParticipantColor(state.participants),
          order: state.participants.length,
          x,
          layerId,
        });
        syncParticipantOrder(state.participants);
        enrollParticipant(state, state.participants[state.participants.length - 1]);
      }),

    updateParticipant: (id, updates) =>
      apply((state) => {
        const idx = state.participants.findIndex((p) => p.id === id);
        if (idx === -1) return;
        Object.assign(state.participants[idx], updates);
        const participant = state.participants[idx];
        if (updates.x !== undefined) syncParticipantOrder(state.participants);
        enrollParticipant(state, participant);
        if (
          participant.catalogId &&
          (updates.label !== undefined || updates.description !== undefined || updates.links !== undefined)
        ) {
          const entry = state.catalog.find(item => item.id === participant.catalogId);
          if (entry) {
            mergeIntoCatalog(entry, {
              name: participant.label,
              description: participant.description,
              links: participant.links,
              type: participant.type,
              color: participant.color,
            });
            syncCatalogAcrossDocument(state, participant.catalogId);
          }
        }
      }),

    removeParticipant: (id) =>
      apply((state) => {
        state.participants = state.participants.filter((p) => p.id !== id);
        state.messages = state.messages.filter((m) => m.sourceId !== id && m.targetId !== id);
        const nextFragments = detachParticipantFromFragments(state.fragments, id);
        const keptIds = new Set(nextFragments.map((fragment) => fragment.id));
        state.messages.forEach((message) => {
          if (message.fragmentId && !keptIds.has(message.fragmentId)) message.fragmentId = undefined;
        });
        nextFragments.forEach((fragment) => {
          if (fragment.parentFragmentId && !keptIds.has(fragment.parentFragmentId)) {
            fragment.parentFragmentId = undefined;
          }
        });
        state.fragments = nextFragments;
        syncParticipantOrder(state.participants);
        state.participantGroups.forEach((g) => {
          g.childParticipantIds = g.childParticipantIds.filter((cid) => cid !== id);
        });
        state.participantGroups = state.participantGroups.filter(
          (g) => g.childParticipantIds.length > 0,
        );
      }),

    reorderParticipants: () =>
      apply((state) => {
        syncParticipantOrder(state.participants);
      }),

    addMessage: (messageData) =>
      apply((state) => {
        const newId = generateId('message');
        const y = messageData.y ?? nextSequenceMessageY(
          state.participants,
          state.messages,
          messageData,
        );
        state.messages.push({ ...messageData, id: newId, y });
        state.messages.sort((a, b) => a.y - b.y);
        if (messageData.fragmentId) {
          recalcFragmentBounds(state, messageData.fragmentId);
        }
      }),

    updateMessage: (id, updates) =>
      apply((state) => {
        const idx = state.messages.findIndex((m) => m.id === id);
        if (idx !== -1) {
          const oldFragId = state.messages[idx].fragmentId;
          Object.assign(state.messages[idx], updates);
          if (updates.y !== undefined) {
            state.messages.sort((a, b) => a.y - b.y);
          }
          if (updates.y !== undefined || updates.fragmentId !== undefined) {
            if (oldFragId) recalcFragmentBounds(state, oldFragId);
            const newFragId = state.messages[idx].fragmentId;
            if (newFragId && newFragId !== oldFragId) recalcFragmentBounds(state, newFragId);
            if (newFragId && newFragId === oldFragId && updates.y !== undefined) {
              recalcFragmentBounds(state, newFragId);
            }
          }
        }
      }),

    applySequenceLayoutPatch: (patch) =>
      apply((state) => {
        const messageYUpdates = patch.messageYUpdates ?? [];
        const messageFragmentUpdates = patch.messageFragmentUpdates ?? [];
        const fragmentUpdates = patch.fragmentUpdates ?? [];
        const shouldSort = patch.sortMessages ?? messageYUpdates.length > 0;
        const shouldRecalcMessageFragments = patch.recalcMessageFragments ?? true;
        const recalcFragIds = new Set<string>(patch.recalcFragmentIds ?? []);

        if (messageYUpdates.length > 0 || messageFragmentUpdates.length > 0) {
          const msgById = new Map(state.messages.map((message) => [message.id, message]));
          messageYUpdates.forEach((update) => {
            const message = msgById.get(update.id);
            if (!message) return;
            message.y = update.y;
            if (shouldRecalcMessageFragments && message.fragmentId)
              recalcFragIds.add(message.fragmentId);
          });
          messageFragmentUpdates.forEach((update) => {
            const message = msgById.get(update.id);
            if (!message) return;
            const oldFragId = message.fragmentId;
            message.fragmentId = update.fragmentId;
            if (shouldRecalcMessageFragments) {
              if (oldFragId) recalcFragIds.add(oldFragId);
              if (update.fragmentId) recalcFragIds.add(update.fragmentId);
            }
          });
        }

        if (shouldSort) {
          state.messages.sort((a, b) => a.y - b.y);
        }

        if (recalcFragIds.size > 0) {
          Array.from(recalcFragIds).forEach((fragId) => recalcFragmentBounds(state, fragId));
        }

        if (fragmentUpdates.length > 0) {
          const fragById = new Map(state.fragments.map((fragment) => [fragment.id, fragment]));
          fragmentUpdates.forEach((update) => {
            const fragment = fragById.get(update.id);
            if (!fragment) return;
            if (update.startY !== undefined) fragment.startY = update.startY;
            if (update.endY !== undefined) fragment.endY = update.endY;
            if (update.altDividerY !== undefined || 'altDividerY' in update) {
              fragment.altDividerY = update.altDividerY;
            }
          });
        }
      }),

    removeMessage: (id) =>
      apply((state) => {
        const msg = state.messages.find((m) => m.id === id);
        const fragId = msg?.fragmentId;
        state.messages = state.messages.filter((m) => m.id !== id);
        if (fragId) recalcFragmentBounds(state, fragId);
      }),

    addFragment: (fragmentData) =>
      apply((state) => {
        const id = generateId('fragment');
        state.fragments.push({ ...fragmentData, id });
        applyFragmentNesting(state, id, fragmentData.parentFragmentId);
        assignBelongingMessagesInFragment(state, id);
        recalcFragmentBounds(state, id);
      }),

    updateFragment: (id, updates) =>
      apply((state) => {
        const idx = state.fragments.findIndex((f) => f.id === id);
        if (idx !== -1) Object.assign(state.fragments[idx], updates);
      }),

    removeFragment: (id) =>
      apply((state) => {
        state.messages.forEach((m) => {
          if (m.fragmentId === id) m.fragmentId = undefined;
        });
        state.fragments.forEach((f) => {
          if (f.parentFragmentId === id) f.parentFragmentId = undefined;
        });
        state.fragments = state.fragments.filter((f) => f.id !== id);
      }),

    moveFragment: (id, deltaY) =>
      apply((state) => {
        if (Math.abs(deltaY) >= 1) {
          moveFragmentRecursive(state, id, deltaY);
          state.messages.sort((a, b) => a.y - b.y);
        }
        const frag = state.fragments.find((f) => f.id === id);
        if (!frag) return;
        const parentId = resolveStickyParentFragmentId(state.fragments, frag);
        applyFragmentNesting(state, id, parentId);
        if (parentId) recalcFragmentBounds(state, parentId);
      }),

    groupParticipants: (participantIds, label) =>
      apply((state) => {
        if (participantIds.length < 2) return;
        const groupId = generateId('pgroup');
        state.participantGroups.forEach((g) => {
          g.childParticipantIds = g.childParticipantIds.filter(
            (gid) => !participantIds.includes(gid),
          );
        });
        state.participantGroups = state.participantGroups.filter(
          (g) => g.childParticipantIds.length > 0,
        );
        const firstP = state.participants.find((p) => participantIds.includes(p.id));
        state.participantGroups.push({
          id: groupId,
          label: label ?? `Group ${state.participantGroups.length + 1}`,
          color: firstP?.color ?? '#64748b',
          childParticipantIds: participantIds,
          collapsed: false,
        });
      }),

    ungroupParticipants: (groupId) =>
      apply((state) => {
        state.participantGroups = state.participantGroups.filter((g) => g.id !== groupId);
      }),

    toggleParticipantGroupCollapse: (groupId) =>
      apply((state) => {
        const group = state.participantGroups.find((g) => g.id === groupId);
        if (group) group.collapsed = !group.collapsed;
      }),

    updateParticipantGroup: (id, updates) =>
      apply((state) => {
        const group = state.participantGroups.find((g) => g.id === id);
        if (group) Object.assign(group, updates);
      }),

    replaceSequenceContent: (data) =>
      apply((state) => {
        state.diagramType = 'sequence';
        state.participants = data.participants.map((participant) => ({ ...participant }));
        state.messages = data.messages.map((message) => ({ ...message }));
        state.fragments = data.fragments.map((fragment) => ({
          ...fragment,
          participantIds: [...fragment.participantIds],
        }));
        state.participantGroups = (data.participantGroups ?? []).map((group) => ({
          ...group,
          childParticipantIds: [...group.childParticipantIds],
        }));
        if (data.seqLifelineLength !== undefined) state.seqLifelineLength = data.seqLifelineLength;
        syncParticipantOrder(state.participants);
        state.participants.forEach((participant) => enrollParticipant(state, participant));
        const page = state.pages.find((entry) => entry.id === state.activePage);
        if (page) page.diagramType = 'sequence';
      }),
  };
}
